import { Dispatch } from '@reduxjs/toolkit';
import { filter, keyBy, uniqBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { List, ListStatus } from 'dashboard/models/list';
import { ListItem, ListItemStatus } from 'dashboard/models/list-item';
import { User } from 'dashboard/models/user';
import { ListFilterParams, saveListAndListDetails } from 'dashboard/redux/actions/list';
import { ListsFilterParams, setLists, setListsDetails } from 'dashboard/redux/actions/lists';
import { ApiEndpoint, ApiModel, PaginationParams } from 'lib/interfaces/api';

interface LoadListsProps {
  currentEnvironment: Environment;
  filters: ListsFilterParams;
  sortParams: SortParams;
  abortSignal?: AbortSignal;
}

export function loadLists({
  currentEnvironment,
  filters,
  sortParams,
  abortSignal,
}: LoadListsProps) {
  return async (dispatch: Dispatch) => {
    const { sort, shouldSortDescending } = sortParams;
    const { status } = filters;

    const params = {
      filter: {
        'environment-id': currentEnvironment?.id,
        status,
      },
      sort: `${shouldSortDescending ? '-' : ''}${sort}`,
      signal: abortSignal,
    };

    const listsResponse = await SessionClientDashboard.listResource<List>(ApiEndpoint.List, params);

    const lists = listsResponse.data;
    const includedItemsMap = keyBy('id', listsResponse.included);

    const allUsers = filter(
      (apiRelationship) => apiRelationship.type === ApiModel.User,
      includedItemsMap,
    ) as User[];
    const users = uniqBy('id', allUsers);

    dispatch(setLists(lists));
    dispatch(setListsDetails(users));
  };
}

interface LoadListProps {
  id: string;
  filters: ListFilterParams;
  currentPage: number;
  pageSize: number;
  currentEnvironment: Environment;
  sort: SortParams;
}

export const loadList = ({
  id,
  filters,
  currentPage,
  pageSize,
  currentEnvironment,
  sort,
}: LoadListProps) => {
  return async (dispatch: Dispatch) => {
    const { status, omni } = filters;
    const listResponse = await SessionClientDashboard.getResource<List>(ApiEndpoint.List, id);
    const list = listResponse.data;

    const listItemsResponse = await SessionClientDashboard.listResource<ListItem, PaginationParams>(
      ApiEndpoint.ListItem,
      {
        filter: {
          'list-id': list.id,
          status:
            list.attributes.status === ListStatus.Archived ? [ListItemStatus.Archived] : status,
          omni,
          'environment-id': currentEnvironment.id,
        },
        page: {
          number: currentPage,
          size: pageSize,
        },
        sort: `${sort.shouldSortDescending ? '-' : ''}${sort.sort}`,
      },
    );

    const listItems = listItemsResponse.data;
    const includedItemsMap = keyBy('id', listItemsResponse.included);

    const allUsers = listItems.map((listItem) => {
      return listItem.relationships.creator.data != null
        ? (includedItemsMap[listItem.relationships.creator.data.id] as User)
        : undefined;
    }) as User[];

    const users = uniqBy('id', allUsers);
    const paginationParams = listItemsResponse.meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(saveListAndListDetails(list, listItems, users, paginationParams));
  };
};
